/**
 * 桌面侧栏收起态解析（SSR / 内联脚本 / composable 共用）。
 *
 * - 偏好：`sidebarCollapsed` + `lip-dashboard-sidebar-user-pref` cookie
 * - 壳层展示：`navCollapsed` = resolveLayoutSidebarCollapsed(sidebarCollapsed)（见 useDashboardShellLayout）
 * - DOM：仅 `syncSidebarCollapsedDomAttr`，属性值仅 `'1'|'0'`
 *
 * @see docs/dashboard-shell-state.md
 */

export const SIDEBAR_COLLAPSED_COOKIE = 'lip-dashboard-sidebar-collapsed'
export const SIDEBAR_USER_PREF_COOKIE = 'lip-dashboard-sidebar-user-pref'
export const LEGACY_SIDEBAR_STORAGE_KEY = 'lip-dashboard-sidebar-collapsed'

export const SIDEBAR_STATE_KEY = 'lip-sidebar-collapsed'
export const SIDEBAR_WATCHES_KEY = 'lip-sidebar-watches-installed'
/** 与 cookie 同步，刷新时兜底（cookie 被拦或 SSR 未带 Cookie 时） */
export const SIDEBAR_USER_PREF_STORAGE_KEY = 'lip-dashboard-sidebar-user-pref'
export const SUB_NAV_COLLAPSED_STORAGE_KEY = 'lip-dashboard-sub-nav-collapsed'

/** 双栏侧栏宽度（px）：一级轨 / 二级 icon 轨（与一级同宽）/ 二级展开 */
export const SIDEBAR_PRIMARY_WIDTH_PX = 92
export const SIDEBAR_SUB_RAIL_WIDTH_PX = 92
export const SIDEBAR_SUB_EXPANDED_WIDTH_PX = 188

/** 移动端抽屉展开宽度（px） */
export const SIDEBAR_MOBILE_DRAWER_WIDTH_PX = 320

/** 桌面侧栏总宽：二级 icon 轨 | 二级展开（双栏模式不再整栏收起） */
export function resolveSidebarWidthPx(_navCollapsed, subNavCollapsed) {
  if (subNavCollapsed) return SIDEBAR_PRIMARY_WIDTH_PX + SIDEBAR_SUB_RAIL_WIDTH_PX
  return SIDEBAR_PRIMARY_WIDTH_PX + SIDEBAR_SUB_EXPANDED_WIDTH_PX
}

/** 移动端抽屉宽度：二级收起时为双轨窄宽，否则全宽面板 */
export function resolveMobileSidebarWidthPx(subNavCollapsed) {
  return subNavCollapsed
    ? SIDEBAR_PRIMARY_WIDTH_PX + SIDEBAR_SUB_RAIL_WIDTH_PX
    : SIDEBAR_MOBILE_DRAWER_WIDTH_PX
}

/** Nuxt useCookie 与手写 document.cookie 共用 */
export const SIDEBAR_COOKIE_OPTIONS = {
  path: '/',
  maxAge: 60 * 60 * 24 * 365,
  sameSite: 'lax',
}

/** 与 `definePageMeta({ sidebarCollapsedDefault: true })` 一致，供 app.html 内联脚本首访默认收起 */
export const COMPACT_ROUTE_PATHS = new Set([
  '/pricing',
  '/terms',
  '/privacy-policy',
  '/refer',
  '/account',
])

const LOCALE_CODES = new Set([
  'en',
  'ru',
  'ja',
  'id',
  'ar',
  'cn',
  'de',
  'es',
  'fr',
  'it',
  'kr',
  'nl',
  'pt',
  'tr',
  'zh',
])

export function normalizeDashboardPath(path) {
  const clean = (path || '/').split('?')[0].split('#')[0]
  const parts = clean.split('/').filter(Boolean)
  if (parts.length && LOCALE_CODES.has(parts[0])) {
    parts.shift()
  }
  return parts.length ? `/${parts.join('/')}` : '/'
}

export function routeWantsCompactByPath(path) {
  return COMPACT_ROUTE_PATHS.has(normalizeDashboardPath(path))
}

/** 仅页面 meta 声明首访收起（对齐 page-quality-checklist 定价页等），非全站默认 rail */
export function routeWantsCompact(targetRoute) {
  return targetRoute?.meta?.sidebarCollapsedDefault === true
}

/**
 * 桌面侧栏是否收起：用户偏好优先；无偏好时 PC 默认展开，仅 `sidebarCollapsedDefault` 页首访收起。
 * @param {import('vue-router').RouteLocationNormalizedLoaded | { path?: string, meta?: Record<string, unknown> }} targetRoute
 * @param {string} pref `collapsed` | `expanded` | ''
 */
export function resolveSidebarCollapsed(targetRoute, pref) {
  if (pref === 'collapsed') return true
  if (pref === 'expanded') return false
  return routeWantsCompact(targetRoute)
}

export function sidebarCollapsedHtmlAttr(collapsed) {
  return collapsed ? '1' : '0'
}

/** 是否按桌面侧栏轨布局（≥768px）；SSR 无 window 时视为桌面以便与 cookie 首屏一致 */
export function isDesktopSidebarViewport() {
  return typeof window === 'undefined' || window.innerWidth >= 768
}

/**
 * 壳层实际收起态：与 dashboard `navCollapsed` 一致（小屏恒展开，不占 rail）。
 * @param {boolean} storedCollapsed `sidebarCollapsed` / cookie 解析结果
 */
export function resolveLayoutSidebarCollapsed(storedCollapsed) {
  if (!isDesktopSidebarViewport()) return false
  return Boolean(storedCollapsed)
}

/** 解析 DOM 上的收起标记（仅认 '1'/'0'；兼容线上残留的 'true'/'false'） */
export function isSidebarCollapsedAttrValue(value) {
  if (value === '1' || value === true || value === 'true') return true
  if (value === '0' || value === false || value === 'false') return false
  return null
}

/** 读取当前文档侧栏是否收起（html 优先，其次 #layout-sidebar） */
export function isSidebarDomCollapsed() {
  if (typeof document === 'undefined') return null
  const fromHtml = isSidebarCollapsedAttrValue(
    document.documentElement.getAttribute('data-sidebar-collapsed'),
  )
  if (fromHtml !== null) return fromHtml
  const el = document.getElementById('layout-sidebar')
  if (!el) return null
  return isSidebarCollapsedAttrValue(el.getAttribute('data-collapsed'))
}

/** 同步 html / body / #layout-sidebar（与组件 `navCollapsed` 同源，小屏写 '0'） */
export function syncSidebarCollapsedDomAttr(storedCollapsed) {
  if (typeof document === 'undefined') return
  const attr = sidebarCollapsedHtmlAttr(resolveLayoutSidebarCollapsed(storedCollapsed))
  document.documentElement.setAttribute('data-sidebar-collapsed', attr)
  document.body?.setAttribute('data-sidebar-collapsed', attr)
  const el = document.getElementById('layout-sidebar')
  if (el) {
    el.setAttribute('data-collapsed', attr)
  }
}

/**
 * 请求里是否带有指定 cookie（须传入 Cookie 头或仅在浏览器用 document.cookie）。
 * 勿在此文件调用 `useRequestHeaders`：本模块会被 client 插件引用，会触发 Transform 失败。
 * @param {string} name
 * @param {string} [cookieHeader] SSR 的 `Cookie` 请求头或 `document.cookie`
 */
export function requestHasCookie(name, cookieHeader = '') {
  if (cookieHeader) {
    return new RegExp(`(?:^|; )${encodeURIComponent(name)}=`).test(cookieHeader)
  }
  if (typeof document !== 'undefined') {
    return document.cookie.split(';').some((part) => part.trim().startsWith(`${name}=`))
  }
  return false
}

/**
 * 线上旧版仅写入 boolean cookie；迁移为 user-pref 后再走路由默认逻辑。
 * @returns {string} 规范化后的 pref（可能已被写入 cookie）
 */
export function normalizeSidebarUserPref(pref, legacyCollapsed) {
  if (pref === 'collapsed' || pref === 'expanded') return pref

  if (typeof legacyCollapsed === 'boolean') {
    return legacyCollapsed ? 'collapsed' : 'expanded'
  }

  return ''
}

/** 解析 `document.cookie` / `Cookie` 请求头为键值表 */
export function parseCookieMap(cookieHeader = '') {
  const map = {}
  if (!cookieHeader) return map
  for (const part of cookieHeader.split(';')) {
    const trimmed = part.trim()
    const eq = trimmed.indexOf('=')
    if (eq < 0) continue
    const key = trimmed.slice(0, eq)
    let val = trimmed.slice(eq + 1)
    try {
      val = decodeURIComponent(val)
    } catch {
      /* keep raw */
    }
    map[key] = val
  }
  return map
}

/** 规范化 cookie / useCookie 可能带上的引号 */
export function normalizeSidebarPrefValue(raw) {
  if (raw == null || raw === '') return ''
  const v = String(raw).replace(/^["']|["']$/g, '').trim()
  return v === 'collapsed' || v === 'expanded' ? v : ''
}

/** 从 Cookie 字符串读取侧栏用户偏好（与 app.html 内联脚本一致） */
export function readSidebarUserPrefFromCookieString(cookieHeader) {
  return normalizeSidebarPrefValue(parseCookieMap(cookieHeader)[SIDEBAR_USER_PREF_COOKIE])
}

export function readSidebarUserPrefFromStorage() {
  if (typeof sessionStorage === 'undefined') return ''
  return normalizeSidebarPrefValue(sessionStorage.getItem(SIDEBAR_USER_PREF_STORAGE_KEY))
}

export function readSidebarUserPrefClient() {
  if (typeof document === 'undefined') return ''
  const fromCookie = readSidebarUserPrefFromCookieString(document.cookie)
  if (fromCookie) return fromCookie
  return readSidebarUserPrefFromStorage()
}

/** 刷新前立即写入偏好（cookie + sessionStorage），避免 useCookie 与 document 不同步 */
export function writeSidebarUserPrefClient(pref) {
  if (typeof document === 'undefined') return
  const normalized = normalizeSidebarPrefValue(pref)
  if (!normalized) return
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${SIDEBAR_USER_PREF_COOKIE}=${encodeURIComponent(normalized)}; Path=${SIDEBAR_COOKIE_OPTIONS.path}; Max-Age=${SIDEBAR_COOKIE_OPTIONS.maxAge}; SameSite=Lax${secure}`
  try {
    sessionStorage.setItem(SIDEBAR_USER_PREF_STORAGE_KEY, normalized)
  } catch {
    /* private mode */
  }
}

/**
 * 解析有效用户偏好：useCookie（会话内最新）→ Cookie 头 → sessionStorage。
 * 切换时须先写 document.cookie 再改 useCookie，否则 watch 会读到旧 cookie 把状态打回。
 * @param {string} cookieHeader SSR `Cookie` 头或 `document.cookie`
 * @param {string} useCookiePref `useCookie` 当前值
 */
export function resolveSidebarUserPref(cookieHeader = '', useCookiePref = '') {
  const fromUseCookie = normalizeSidebarPrefValue(useCookiePref)
  if (fromUseCookie) return fromUseCookie
  const fromHeader = readSidebarUserPrefFromCookieString(cookieHeader)
  if (fromHeader) return fromHeader
  if (typeof sessionStorage !== 'undefined') {
    return readSidebarUserPrefFromStorage()
  }
  return ''
}
